import { IAxiosError } from '@/@types/AxiosError';
import { UserTypeEnum } from '@/@types/User';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addHours,
  addMinutes,
  secondsToHours,
  secondsToMinutes,
} from 'date-fns';
import flagsmith from 'flagsmith';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppRouter } from '@/common/hooks/useAppRouter';
import { useUserStore } from '@/common/hooks/UserStore';
import { useToast } from '@/common/hooks/useToast';
import { getUserInfo } from '@/common/services/getUserInfo';
import { links } from '@/common/utils/links';
import { httpClient } from '@/config/httpClient';
import {
  ISignInResponse,
  signInService,
} from '@/modules/login/services/signIn';
import { ProtectedRoutes } from '@/routes/protected.routes';
import { PublicRoutes } from '@/routes/public.routes';

import { SignInForm } from '../schemas/SignInSchema';

export type GetTokenCredentials = {
  username: string;
  password: string;
  grant_type: string;
};

export function useLogin() {
  const { mutateAsync: mutateSignIn, isLoading: isLoadingSignIn } =
    useMutation(signInService);
  const navigate = useNavigate();
  const { changeRouter } = useAppRouter();
  const {
    saveAccountUser,
    saveDealerUser,
    saveUserStore,
    saveUserAccount,
    removeUser,
  } = useUserStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('login/signIn');

  const saveToken = (data: ISignInResponse, rememberData?: boolean) => {
    httpClient.defaults.headers.Authorization = `Bearer ${data.access_token}`;

    if (!rememberData) return;

    Cookies.set('accessToken', data.access_token, {
      expires: addHours(new Date(), secondsToHours(data.expires_in)),
    });
    Cookies.set('refreshToken', data.refresh_token, {
      expires: addMinutes(
        new Date(),
        secondsToMinutes(data.refresh_expires_in),
      ),
    });
    Cookies.set('sessionState', data.session_state);
  };

  const signIn = async (data: SignInForm) => {
    try {
      const credentials: GetTokenCredentials = {
        username: data.username,
        password: data.password,
        grant_type: 'password',
      };

      const response = await mutateSignIn(credentials);

      if (!response) {
        return toast.error(t('invalidCredentials'));
      }

      saveToken(response.data, data.rememberData);
    } catch (error) {
      const requestError = error as IAxiosError;

      toast.error(
        requestError?.response?.data?.friendlyMessage || t('signInError'),
      );
    }
  };

  const identifyUser = async () => {
    try {
      const response = await getUserInfo();
      const user = response.data;

      if (!user.isTermAccepted) {
        toast.warn(t('login/signIn:termsOfUseNotAcceptedYet'));
        window.HSOverlay.open(
          document.getElementById('hs-modal-terms-of-use')!,
        );
        return;
      }

      await flagsmith.identify(user?.email, { userType: user.userType });
      await flagsmith.getFlags();
      flagsmith.startListening(
        1000 * 60 * 15, // 15 minutes
      );

      if (user.userType === UserTypeEnum.ACCOUNT) {
        const userAccounts = saveAccountUser(user);

        Cookies.set('userType', UserTypeEnum.ACCOUNT);

        if (userAccounts.length === 1) {
          const { accountId, role } = userAccounts[0];

          Cookies.set('accountId', accountId);
          Cookies.set('role', role);

          await flagsmith.setTrait('role', role);

          saveUserAccount({
            accountId,
            role,
          });
          changeRouter(ProtectedRoutes);
          navigate(links.panel, {
            replace: true,
          });
          return;
        }

        navigate(links.login.accountSelection, {
          replace: true,
        });
      } else {
        const userStores = saveDealerUser(user);
        Cookies.set('userType', UserTypeEnum.DEALER);

        if (userStores.length === 1) {
          const { dealerId, id, role, dealerStoreUserId, Dealer } =
            userStores[0];
          Cookies.set('accountId', Dealer.Account.id);
          Cookies.set('dealerId', dealerId);
          Cookies.set('storeId', id);
          Cookies.set('role', role);

          await flagsmith.setTrait('dealerId', dealerId);
          // USUÁRIOS "STORE" POSSUI A VARIÁVEL `storeUser` = `true`
          await flagsmith.setTrait('storeUser', !!dealerStoreUserId);
          await flagsmith.setTrait('role', role);

          saveUserAccount({
            accountId: Dealer.Account.id,
            role,
          });
          saveUserStore({
            dealerId,
            storeId: id,
            role,
          });
          changeRouter(ProtectedRoutes);
          navigate(links.panel, {
            replace: true,
          });
          return;
        }

        navigate(links.login.storeSelection, {
          replace: true,
        });
      }
    } catch (error) {
      const requestError = error as IAxiosError;

      toast.error(
        requestError?.response?.data?.friendlyMessage ||
          t('login/signIn:signInError'),
      );
    }
  };

  const signOut = async () => {
    changeRouter(PublicRoutes);
    removeUser();
    navigate('/', {
      replace: true,
    });

    flagsmith.logout();
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    Cookies.remove('sessionState');
    Cookies.remove('dealerId');
    Cookies.remove('accountId');
    Cookies.remove('storeId');
    Cookies.remove('role');
    Cookies.remove('userType');

    httpClient.defaults.headers.Authorization = '';
    queryClient.removeQueries();
  };

  return {
    signIn,
    identifyUser,
    saveToken,
    signOut,
    isLoadingSignIn,
  };
}
